import { saga, SagaReduxHttpRequestWithBody } from "@api/common";
import {
    AdUnitFormat,
    AgeMapping,
    BackendBuildInfo,
    CampaignInfo,
    CategoryMapping,
    ClientPostData,
    ClientsListData,
    Country,
    CurrencyMapping,
    CustomerData,
    DeviceMapping,
    EstimatedImpressionData,
    GenderMapping,
    IndustryMapping,
    InterestMapping,
    LanguagesMapping,
    PendingCampaign,
    Place,
    RestrictionMapping,
    SignupFormData,
    SupportData,
    SupportMessage,
    TimezoneMapping,
    UserAccountInfo,
    UserPostData,
    KPIMapping
} from "@store";
import { http, reduxHttpMiddlewareFactory } from "rd-redux-http";
import { apiUrl, customFetch, prepareRequest } from "../api/utils";
export const reduxHttpMiddleware = reduxHttpMiddlewareFactory();

export const getCampaignsListRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get<{ clientId: string; _q: number }>(apiUrl("campaigns/:clientId"), true, "getCampaignList")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<CampaignInfo[]>()
            .build()
    )
);

export const postCampaignRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .post<{}>(apiUrl("campaigns"), false, "postCampaignRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .jsonBody<PendingCampaign>()
            .resultFromJson<{ campaignId: string }>()
            .build()
    )
);

export const purchaseCampaignByIdRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .put<{ clientId: string; campaignId: string }>(
                apiUrl("campaigns/purchase"),
                true,
                "purchaseCampaignByIdRequest"
            )
            .pre(prepareRequest)
            .withFetch(customFetch)
            .convertResult(r => Promise.resolve(r))
            .build()
    )
);

export const updateCampaignByIdRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .put<{ clientId: string; campaignId: string }>(apiUrl("campaigns"), true, "updateCampaignByIdRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .jsonBody<PendingCampaign>()
            .resultFromJson<{ result: string }>()
            .build()
    )
);

/**
 * Because on app backend the data of a purchase campaign is deleted after the purchase
 * we need to retrieve the campaign data from the CRM using the list camapaign endpoint plus the camapiagn ID as query param
 */
export const getPurchasedCampaignDataFromCRM = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get<{ clientId: string; campaignId: string; _q: number }>(
                apiUrl("campaigns/:clientId"),
                true,
                "getPurchasedCampaignDataFromCRM"
            )
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson()
            .build()
    )
);

export const generatePDFOfferRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get<{ clientId: string; campaignId: string }>(
                apiUrl("campaigns-offers/clients/:clientId"),
                true,
                "downloadPdfOfferRequest"
            )
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<{ content: string }>()
            .build()
    )
);

export const getCampaignByIdRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get<{ clientId: string; campaignId: string }>(apiUrl("campaigns"), true, "getCampaignByIdRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<{ result: PendingCampaign }>()
            .build()
    )
);

export const getCountryListRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get(apiUrl("geo/areas"), false, "getCountryListRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<Country[]>()
            .build()
    )
);

export const getSearchLocationRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get<{ "search-zip"?: string; "search-place"?: string; region: string }>(
                apiUrl("geo/location/:region"),
                true,
                "getSearchLocationRequest"
            )
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<Place[]>()
            .build()
    )
);

export const getClientsRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get(apiUrl("clients"), false, "getClientsRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<ClientsListData>()
            .build()
    )
);

export const getCustomerRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get(apiUrl("/customer"), false, "getCustomerRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<CustomerData>()
            .build()
    )
);

export const removeCampaignByIdRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .delete<{ campaignId: string; clientId: string }>(
                apiUrl("campaigns/:campaignId"),
                true,
                "removeCampaignByIdRequest"
            )
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<{ result: boolean }>()
            .build()
    )
);

export const calculateEstimatedImpressionsRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .post<{ requestId: number }>(
                apiUrl("/campaigns/estimated-audience"),
                false,
                "calculateEstimatedImpressionsRequest"
            )
            .pre(prepareRequest)
            .withFetch(customFetch)
            .jsonBody<PendingCampaign>()
            .resultFromJson<EstimatedImpressionData>()
            .build()
    )
);

export const uploadImageToBucketRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .post<{}>(apiUrl("/ad-units/create-file-upload"), true, "uploadImageToBucketRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .jsonBody<{ fileName: string }>()
            .resultFromJson<{ putUrl: string; getUrl: string }>()
            .build()
    )
);

export const postClientRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .post<{}>(apiUrl("/clients"), false, "postClientRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .jsonBody<ClientPostData>()
            .resultFromJson<{ clientId: string }>()
            .build()
    )
);

export const downloadExcelRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get<{ campaignId: string }>(apiUrl("/reporting/static/:campaignId"), false, "downloadExcelRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<{ content: string }>()
            .build()
    )
);

export const downloadGeographicCountrySvg = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get<{ url: string }>(params => params.url, false, "downloadGeographicCountrySvg")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .convertResult(r => r.text().then(text => Promise.resolve(text)))
            .build()
    )
);

export const postSupportMessageRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .post(apiUrl("/support"), false, "postSupportMessageRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .jsonBody<SupportMessage>()
            .resultFromJson<SupportData>()
            .build()
    )
);

export const getBackendVersionRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get(apiUrl("/version"), false, "getVersionRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<BackendBuildInfo>()
            .build()
    )
);

export const postSignupRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .post(apiUrl("/signup"), false, "postSignupRequest")
            .jsonBody<SignupFormData>()
            .convertResult(r => Promise.resolve(r))
            .build()
    )
);

export const getUsersRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get(apiUrl("/users"), false, "getUsersRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<UserAccountInfo[]>()
            .build()
    )
);

export const postUserRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .post(apiUrl("/users"), false, "getUsersRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .jsonBody<UserPostData>()
            .resultFromJson<UserAccountInfo>()
            .build()
    )
);

export const deleteUserRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .delete<{ userId: string }>(apiUrl("/users/:userId"), false, "deleteUserRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .convertResult(r => Promise.resolve(r))
            .build()
    )
);

/* Mappings */

export const getAdUnitAndFormatInfoRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get(apiUrl("/mappings/adunits"), false, "getAdUnitAndFormatInfoRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<AdUnitFormat[]>()
            .build()
    )
);

export const getKPIRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get(apiUrl("/mappings/objectives"), false, "getKPIRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<KPIMapping[]>()
            .build()
    )
);

export const getDevicesRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get(apiUrl("/mappings/devices"), false, "getDevicesRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<DeviceMapping[]>()
            .build()
    )
);

export const getInterestsRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get(apiUrl("/mappings/interests"), false, "getInterestsRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<InterestMapping[]>()
            .build()
    )
);

export const getAgeRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get(apiUrl("/mappings/age"), false, "getAgeRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<AgeMapping[]>()
            .build()
    )
);

export const getGenderRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get(apiUrl("/mappings/gender"), false, "getGenderRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<GenderMapping[]>()
            .build()
    )
);

export const getLanguagesRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get(apiUrl("/mappings/languages"), false, "getLanguagesRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<LanguagesMapping[]>()
            .build()
    )
);

export const getCurrenciesRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get(apiUrl("/mappings/currencies"), false, "getCurrenciesRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<CurrencyMapping[]>()
            .build()
    )
);

export const getTimezonesRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get(apiUrl("/mappings/timezones"), false, "getTimezonesRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<TimezoneMapping[]>()
            .build()
    )
);

export const getIndustriesRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get(apiUrl("/signup/industries"), false, "getIndustriesRequest")
            // .pre(prepareRequest)
            // .withFetch(customFetch)
            .resultFromJson<IndustryMapping[]>()
            .build()
    )
);

export const getCategoriesRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get(apiUrl("/signup/categories"), false, "getCategoriesRequest")
            // .pre(prepareRequest)
            // .withFetch(customFetch)
            .resultFromJson<CategoryMapping[]>()
            .build()
    )
);

export const getRestrictionsRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get(apiUrl("/mappings/restrictions"), false, "getRestrictionsRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<RestrictionMapping[]>()
            .build()
    )
);

/*********/

export interface CampaignSpentBudget {
    campaignId: string;
    budget: number;
    currency: string;

    budgetUSD: number;
    budgetSpentUSD: number;

    spentPart: number;
    budgetSpentCampaignCurrency: number;
}

export const getCampaignSpentBudgetRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get<{ campaignId: string; clientId: string }>(
                apiUrl("reporting/spent-budget/:clientId/:campaignId"),
                true,
                "getCampaignSpentBudgetRequest"
            )
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<CampaignSpentBudget>()
            .build()
    )
);

export const buildAdUnitRequest: SagaReduxHttpRequestWithBody<
    BuildAdUnitRequest,
    any,
    NewAdUnitInfo,
    AdUnitCreationError
> = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .post<{ clientId: string }>(apiUrl("/ad-units/:clientId"), false, "buildAdUnitRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .jsonBody<BuildAdUnitRequest>()
            .resultFromJson<NewAdUnitInfo, AdUnitCreationError>()
            .build()
    )
);

export interface BuildAdUnitRequest {
    adType: string;
    adFormat: string;
    [assetName: string]: string | object;
}

export interface NewAdUnitInfo {
    ok: true;
    status: "complete" | "pending" | "error";
    id: string;
    name: string;
    links: Link<string>[];
}

export interface AdUnitCreationError {
    ok: false;
    error: string;
}
export const getAdUnitsListRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get<{ clientId: string }>(apiUrl("/ad-units/:clientId/"), false, "getAdUnitListRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<AdUnitInfo[]>()
            .build()
    )
);

export const getAdUnitByIdRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get<{ clientId: string; unitId: string }>(apiUrl("/ad-units/:clientId/job"), true, "getAdUnitByIdRequest")
            .pre(prepareRequest)
            .withFetch(customFetch)
            .resultFromJson<AdUnitInfo>()
            .build()
    )
);

export interface AdUnitInfo {
    id: string;
    name: string;
    customerId: string;
    clientId: string;
    adFormatName: string;
    adFormatSize: string;
    adType: string;
    buildStage: string;

    links: Link<"preview" | "demo" | "screenshot" | "ops">[];
}

export interface Link<TRel> {
    rel: TRel;
    href: string;
}

export type Tours = "campaign-media-plan" | "campaign-creatives" | "campaign-review" | "dashboard";

export type TourConfig = { [P in Tours]?: StepConfig[] };

export interface StepConfig {
    target: string;
    content: string;
}

export const getTourConfigRequest = saga.withSaga(
    reduxHttpMiddleware.register(
        http
            .get("/tour.json")
            .resultFromJson<TourConfig>()
            .build()
    )
);
